import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react'
import { graphql } from '@apollo/client/react/hoc'
import { NetworkStatus, useQuery } from '@apollo/client'
import { useSearchParams, useNavigate, useLocation, Link } from 'react-router-dom'
import { Badge, Button } from 'reactstrap'
import moment from 'moment'
import { AllTasksQuery, BoardQuery } from './Queries'
import { UpdateTaskMutation } from './Mutations'
import TaskDetailsSidePanel from './TaskDetailsSidePanel'
import withApolloClient from '../withApolloClient'
import RepAvatar from '../Material/RepAvatar'
import { buildTaskDetailLocation } from './taskNavigation'
import { boardScopeFilters } from './taskFilterPresets'
import { CUSTOM_FK_CONFIG } from './TaskComponents/ForeignKeyComponents'
import { formatRelatedObjectLabel, getRelatedObjectIcon, getTaskRelatedObjects } from './columnTypeMeta'
import './TaskKanbanView.css'

const KANBAN_COLUMNS = [
    { key: 'OPEN', label: 'Open', mutationValue: 'OPEN' },
    { key: 'IN_PROGRESS', label: 'In Progress', mutationValue: 'IN_PROGRESS' },
    { key: 'DONE', label: 'Done', mutationValue: 'DONE' },
    { key: 'INCOMPLETE', label: 'Incomplete', mutationValue: 'INCOMPLETE' },
]

const PAGE_SIZE = 30
const EMPTY_ENUM_GROUP_KEY = '__EMPTY_ENUM_GROUP__'

const getStatusColumnKey = (status) => status?.toUpperCase().replace(/ /g, '_') || 'OPEN'

// Which column a task belongs in, for whatever the board is grouped by. Status normalises to
// the fixed column keys; a custom column groups by its stored text value, and anything unset
// lands in the unassigned column -- the same notion of "empty" the is_null filter applies
// server-side, so a task is never in one place on the client and another in the query.
const getTaskGroupKey = (task, groupBy) => {
    if (groupBy === 'status') return getStatusColumnKey(task?.status)

    const columnValue = task?.taskcolumnvalueSet?.edges?.find(
        ({ node }) => String(node?.column?.pk) === String(groupBy)
    )
    return columnValue?.node?.valueString || EMPTY_ENUM_GROUP_KEY
}

// Optimistic local copy of a task with one custom column value replaced -- the counterpart to
// patching `status` when the board groups by status. An empty value drops the entry, matching
// what the mutation does with it server-side.
const withCustomColumnValue = (task, column, value) => {
    const otherEdges = (task?.taskcolumnvalueSet?.edges || []).filter(
        ({ node }) => String(node?.column?.pk) !== String(column.pk)
    )
    const edges = value === ''
        ? otherEdges
        : [...otherEdges, { node: { column: { id: column.id, pk: column.pk }, value, valueString: value } }]

    return { ...task, taskcolumnvalueSet: { ...(task?.taskcolumnvalueSet || {}), edges } }
}

// The columns a board is split into, each carrying the filter that fills it. Status columns are
// fixed; a custom column contributes one per allowed option.
const buildGroupColumns = (groupedColumn, groupedFieldName) => {
    if (!groupedColumn) {
        return KANBAN_COLUMNS.map((column) => ({
            ...column,
            filter: { fieldName: 'status', filterType: 'equal', filterValues: column.mutationValue },
        }))
    }

    return (groupedColumn.allowedOptions || []).map((option) => ({
        key: option,
        label: option,
        filter: { fieldName: groupedFieldName, filterType: 'equal', filterValues: option },
    }))
}

const getComparableTaskSignature = (task) => {
    if (!task) return null

    return JSON.stringify({
        id: task.id,
        title: task.title,
        status: task.status,
        dueDate: task.dueDate,
        commentsCount: task.commentsCount,
        dateModified: task.dateModified,
        assignedToIds: task.assignedTo?.edges?.map(({ node }) => node?.id).filter(Boolean) || [],
        columnValues: task.taskcolumnvalueSet?.edges?.map(({ node }) => ({
            columnId: node?.column?.id,
            value: node?.value,
            valueString: node?.valueString,
        })) || [],
    })
}

const getStatusLabel = (status) => {
    const columnKey = getStatusColumnKey(status)
    const knownColumn = KANBAN_COLUMNS.find((column) => column.key === columnKey)
    if (knownColumn) return knownColumn.label
    // Statuses the board doesn't lay out as columns -- CANCELED, say -- still have to read as
    // something, so fall back to the raw value in the same shape as the fixed labels.
    return columnKey.toLowerCase().replace(/_/g, ' ').replace(/^\w/, (character) => character.toUpperCase())
}

// One FK_OBJECT column value on a card. Icon and link come from the same config the My Tasks
// list uses, so a driver looks and links the same in both places. The column title is the
// tooltip rather than inline text -- a card has no room for "Damaged car: 2019 Camry #4821".
function KanbanCardRelatedObject({ relatedObject }) {
    const config = CUSTOM_FK_CONFIG[relatedObject?.model]
    const value = relatedObject?.valueString
    const tooltip = `${formatRelatedObjectLabel(relatedObject)}: ${value}`
    const icon = <i className={`fa ${getRelatedObjectIcon(relatedObject?.model)}`} aria-hidden="true" />

    // draggable={false} so grabbing a chip drags the card rather than the browser's own link
    // drag, and the click is stopped so following it doesn't also open the task panel.
    if (config) {
        return (
            <Link
                className="kanban-card-chip is-linked"
                to={config.getLink(relatedObject)}
                title={tooltip}
                draggable={false}
                onClick={(e) => e.stopPropagation()}
            >
                {icon}
                <span className="kanban-card-chip-value">{value}</span>
            </Link>
        )
    }

    // Same fallback as RelatedObjectsCell: a model with no link config still shows its value.
    return (
        <span className="kanban-card-chip" title={tooltip}>
            {icon}
            <span className="kanban-card-chip-value">{value}</span>
        </span>
    )
}

const getDueDateConfig = (dueDate) => {
    if (!dueDate) return { label: "", isPastDue: false, iconClassName: "" }

    const today = moment().startOf("day")
    const due = moment(dueDate).startOf("day")
    const dayDiff = due.diff(today, "days")

    if (dayDiff === 0) {
        return { label: "Today", isPastDue: false, iconClassName: "fa fa-circle" }
    }
    if (dayDiff > 0) {
        return {
            label: `${dayDiff} Day${dayDiff === 1 ? "" : "s"} Left`,
            isPastDue: false,
            iconClassName: dayDiff < 7 ? "fa fa-circle-o text-muted" : "fa fa-clock-o text-muted",
        }
    }
    if (dayDiff === -1) {
        return { label: "Due Yesterday", isPastDue: true, iconClassName: "fa fa-exclamation-circle text-danger" }
    }

    const overdueDays = Math.abs(dayDiff)
    return {
        label: `${overdueDays} Day${overdueDays === 1 ? "" : "s"} Overdue`,
        isPastDue: true,
        iconClassName: "fa fa-exclamation-circle text-danger",
    }
}

function KanbanColumn({
    column,
    groupBy,
    isHidden = false,
    onColumnTotal,
    location,
    boardId,
    searchTerm,
    incidentId,
    orderBy,
    filters,
    localOverrides,
    taskRegistry,
    mutationLoadingMap,
    onTasksLoaded,
    dragOverColumn,
    onDragOverColumn,
    onDrop,
    draggedTask,
    onDragStart,
    onDragEnd,
    onCardClick,
    onQuickUpdate,
    onStartEditTitle,
    editingTaskId,
    editTaskTitle,
    setEditTaskTitle,
    saveTaskTitle,
    cardMenuTaskId,
    setCardMenuTaskId,
    menuRef,
    dragJustEndedRef,
    enumColumns,
}) {
    const { data, loading, fetchMore, networkStatus } = useQuery(AllTasksQuery, {
        variables: {
            incidentId,
            searchTerm,
            orderBy: ["-dateModified", ...orderBy],
            // The column IS the value of whatever the board groups by, and the board IS the
            // view, so both are composed here rather than chosen by the user, and neither
            // shows in the popup or the chips.
            filters: [
                ...(filters || []),
                ...boardScopeFilters(boardId),
                column.filter,
            ],
            first: PAGE_SIZE,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network',
    })

    const queryTasks = useMemo(
        () => data?.allTasks?.edges?.map(({ node }) => node) || [],
        [data]
    )
    const pageInfo = data?.allTasks?.pageInfo
    const isInitialLoading = loading && !data?.allTasks
    const isFetchMoreLoading = networkStatus === NetworkStatus.fetchMore
    const isMutationLoading = !!mutationLoadingMap[column.key]
    const showColumnLoader = isInitialLoading || isFetchMoreLoading || isMutationLoading

    useEffect(() => {
        onTasksLoaded(column.key, queryTasks)
    }, [column.key, queryTasks, onTasksLoaded])

    // Reported up so the board can decide whether a column is worth showing at all -- the
    // unassigned column stays mounted either way, since unmounting it would drop the very
    // count that decides its visibility.
    useEffect(() => {
        onColumnTotal?.(column.key, pageInfo?.length || 0)
    }, [column.key, pageInfo?.length, onColumnTotal])

    const overriddenIntoColumn = useMemo(() => {
        const tasks = Object.entries(localOverrides)
            .filter(([, targetColumnKey]) => targetColumnKey === column.key)
            .map(([taskId]) => taskRegistry[taskId])
            .filter(Boolean)

        const existingIds = new Set(queryTasks.map((task) => task.id))
        return tasks.filter((task) => !existingIds.has(task.id))
    }, [column.key, localOverrides, queryTasks, taskRegistry])

    const visibleTasks = useMemo(() => {
        const filteredQueryTasks = queryTasks.filter((task) => {
            const overrideColumn = localOverrides[task.id]
            if (overrideColumn) return overrideColumn === column.key
            return getTaskGroupKey(task, groupBy) === column.key
        })

        return [...overriddenIntoColumn, ...filteredQueryTasks]
    }, [column.key, groupBy, localOverrides, overriddenIntoColumn, queryTasks])

    const loadMore = () => {
        if (!pageInfo?.hasNextPage) return
        fetchMore({
            variables: {
                after: pageInfo.endCursor,
                first: PAGE_SIZE,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                if (!fetchMoreResult?.allTasks) return previousResult
                return {
                    allTasks: {
                        ...fetchMoreResult.allTasks,
                        edges: [
                            ...(previousResult?.allTasks?.edges || []),
                            ...(fetchMoreResult?.allTasks?.edges || []),
                        ],
                    },
                }
            },
        })
    }

    return (
        <div
            className={`kanban-column${dragOverColumn === column.key ? ' drag-over' : ''}${isHidden ? ' kanban-column--hidden' : ''}`}
            onDragOver={(e) => {
                e.preventDefault()
                if (draggedTask) onDragOverColumn(column.key)
            }}
            onDragLeave={() => {
                if (dragOverColumn === column.key) onDragOverColumn(null)
            }}
            onDrop={() => onDrop(column.key)}
        >
            <div className="kanban-column-header">
                <span className={`status-label status-${column.key.toLowerCase()}`}>{column.label}</span>
                <Badge color="secondary" className="ml-2">{pageInfo?.length || 0}</Badge>
            </div>
            {showColumnLoader && (
                <div className="kanban-column-loading-bar">
                    <div className="kanban-loading-bar-fill" />
                </div>
            )}
            <div className="kanban-column-body">
                {visibleTasks.length === 0 && !showColumnLoader && (
                    <div className="kanban-empty">No tasks</div>
                )}
                {visibleTasks.map((task) => {
                    const dueDateConfig = getDueDateConfig(task.dueDate)
                    const relatedObjects = getTaskRelatedObjects(task)
                    // Grouped by status, the column heading already is the status -- repeating
                    // it on every card in the column says nothing.
                    const showStatus = groupBy !== 'status'
                    return (
                        <div
                            key={task.id}
                            className={`kanban-card${draggedTask?.id === task.id ? ' dragging' : ''}`}
                            draggable={!task.board?.isArchived && editingTaskId !== task.id}
                            onDragStart={() => {
                                if (task.board?.isArchived || editingTaskId === task.id) return
                                dragJustEndedRef.current = false
                                onDragStart(task)
                            }}
                            onDragEnd={() => {
                                dragJustEndedRef.current = true
                                setTimeout(() => { dragJustEndedRef.current = false }, 200)
                                onDragEnd()
                            }}
                            onClick={() => {
                                if (dragJustEndedRef.current || editingTaskId === task.id) return
                                onCardClick(task.id)
                            }}
                        >
                            <div className="kanban-card-top">
                                {editingTaskId === task.id ? (
                                    <input
                                        className="kanban-title-input"
                                        value={editTaskTitle}
                                        autoFocus
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => setEditTaskTitle(e.target.value)}
                                        onBlur={() => saveTaskTitle(task.id)}
                                        onKeyDown={e => {
                                            e.stopPropagation()
                                            if (e.key === 'Enter') saveTaskTitle(task.id)
                                            if (e.key === 'Escape') onStartEditTitle(null, '')
                                        }}
                                    />
                                ) : (
                                    <div
                                        className="kanban-card-title"
                                        onClick={e => {
                                            if (task.isAutomated) return
                                            e.stopPropagation()
                                            onStartEditTitle(task.id, task.title)
                                        }}
                                        title={task.isAutomated ? "Automated task titles cannot be edited" : "Click to edit title"}
                                    >
                                        {task.title}
                                    </div>
                                )}
                                <div
                                    className="kanban-card-menu-btn"
                                    ref={cardMenuTaskId === task.id ? menuRef : null}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <span className="kanban-card-menu-trigger" onClick={() => setCardMenuTaskId(cardMenuTaskId === task.id ? null : task.id)}>
                                        <i className="fa fa-ellipsis-v" />
                                    </span>
                                    {cardMenuTaskId === task.id && (
                                        <div className="kanban-card-menu-dropdown">
                                            <div className="kanban-card-menu-section-label">Status</div>
                                            {KANBAN_COLUMNS.map((statusColumn) => (
                                                <div
                                                    key={statusColumn.key}
                                                    className={`kanban-card-menu-option${task.status?.toUpperCase() === statusColumn.key ? ' active' : ''}${task.isAutomated && statusColumn.key === 'DONE' ? ' disabled' : ''}`}
                                                    onClick={() => {
                                                        if (task.isAutomated && statusColumn.key === 'DONE') return
                                                        onQuickUpdate(task.id, { status: statusColumn.mutationValue })
                                                        setCardMenuTaskId(null)
                                                    }}
                                                >
                                                    {statusColumn.label}
                                                </div>
                                            ))}
                                            {enumColumns.map((enumCol) => (
                                                <Fragment key={enumCol.pk}>
                                                    <div className="kanban-card-menu-divider" />
                                                    <div className="kanban-card-menu-section-label">{enumCol.title}</div>
                                                    {(enumCol.allowedOptions || []).map((opt) => {
                                                        const currentVal = task.taskcolumnvalueSet?.edges?.find(
                                                            ({ node }) => String(node.column.pk) === String(enumCol.pk)
                                                        )?.node?.valueString
                                                        return (
                                                            <div
                                                                key={opt}
                                                                className={`kanban-card-menu-option${currentVal === opt ? ' active' : ''}`}
                                                                onClick={() => {
                                                                    onQuickUpdate(task.id, { columnValues: [{ columnId: enumCol.id, value: opt }] })
                                                                    setCardMenuTaskId(null)
                                                                }}
                                                            >
                                                                {opt}
                                                            </div>
                                                        )
                                                    })}
                                                </Fragment>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {relatedObjects.length > 0 && (
                                <div className="kanban-card-chips">
                                    {relatedObjects.map((relatedObject) => (
                                        <KanbanCardRelatedObject
                                            key={relatedObject.columnId}
                                            relatedObject={relatedObject}
                                        />
                                    ))}
                                </div>
                            )}
                            <div className="kanban-card-bottom">
                                <div className="kanban-card-footer">
                                    {showStatus && (
                                        <span className={`kanban-card-chip is-status status-${getStatusColumnKey(task.status).toLowerCase()}`}>
                                            {getStatusLabel(task.status)}
                                        </span>
                                    )}
                                    {task.isAutomated && (
                                        <span className="kanban-automated" title="Automated task">
                                            <i className="fa fa-cogs task-automated-icon" aria-hidden="true" />
                                        </span>
                                    )}
                                    {task.dueDate && (
                                        <span
                                            className={`kanban-due-date${dueDateConfig.isPastDue ? ' text-danger' : ''}`}
                                            title={dueDateConfig.label}
                                            style={task.status === "DONE" ? { textDecoration: "line-through" } : {}}
                                        >
                                            <i className={dueDateConfig.iconClassName} aria-hidden="true" />{' '}
                                            {moment(task.dueDate).format('MMM D, YYYY')}
                                        </span>
                                    )}
                                    {task.commentsCount > 0 && (
                                        <span className="kanban-comments">
                                            <i className="fa fa-comments" aria-hidden="true" /> {task.commentsCount}
                                        </span>
                                    )}
                                </div>
                                {task.assignedTo?.edges?.length > 0 && (
                                    <div className="kanban-avatars-stack" onClick={e => e.stopPropagation()}>
                                        {task.assignedTo.edges.slice(0, 3).map(({ node: assignee }, idx) => (
                                            <RepAvatar key={idx} rep={assignee} keepClose />
                                        ))}
                                        {task.assignedTo.edges.length > 3 && (
                                            <span className="kanban-avatar-overflow">+{task.assignedTo.edges.length - 3}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>
            <div className="kanban-column-footer">
                <span className="kanban-column-footer__count">
                    Showing {visibleTasks.length} of {pageInfo?.length || 0}
                </span>
                {pageInfo?.hasNextPage && (
                    <Button color="link" className="kanban-column-footer__more" onClick={loadMore}>
                        Show More
                    </Button>
                )}
            </div>
        </div>
    )
}

function TaskKanbanView({
    board,
    boardLoading,
    apolloClient,
    boardId,
    groupBy = 'status',
    searchTerm,
    incidentId,
    orderBy,
    filters,
}) {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const location = useLocation()
    const selectedTaskId = searchParams.get('task_id')

    const [draggedTask, setDraggedTask] = useState(null)
    const [dragOverColumn, setDragOverColumn] = useState(null)
    const [localOverrides, setLocalOverrides] = useState({})
    const [cardMenuTaskId, setCardMenuTaskId] = useState(null)
    const [editingTaskId, setEditingTaskId] = useState(null)
    const [editTaskTitle, setEditTaskTitle] = useState("")
    const [taskRegistry, setTaskRegistry] = useState({})
    const [mutationLoadingMap, setMutationLoadingMap] = useState({})
    const [unassignedTotal, setUnassignedTotal] = useState(0)
    const menuRef = useRef(null)
    const dragJustEndedRef = useRef(false)

    useEffect(() => {
        if (!cardMenuTaskId) return
        const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setCardMenuTaskId(null) }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [cardMenuTaskId])

    // Memoised because the grouped column, and the column descriptors built from it, are
    // derived from this -- rebuilding the array every render would defeat those.
    const enumColumns = useMemo(() => board?.columnSet?.edges
        ?.filter(({ node }) => node.columnType === 'ENUM' && !node.isArchived)
        .map(({ node }) => node) || [], [board?.columnSet])

    // A board grouped by a custom column cannot be laid out until the board's columns arrive,
    // and a column that has since been archived or deleted leaves nothing to group by -- both
    // fall back to status rather than rendering columns that would query for nothing.
    const groupedColumn = groupBy === 'status'
        ? null
        : enumColumns.find((column) => String(column.pk) === String(groupBy)) || null
    const isAwaitingGroupedColumn = groupBy !== 'status' && !groupedColumn && boardLoading
    const groupedFieldName = groupedColumn ? `custom_column_${groupedColumn.pk}` : 'status'

    const columns = useMemo(
        () => buildGroupColumns(groupedColumn, groupedFieldName),
        [groupedColumn, groupedFieldName]
    )

    // Tasks with no value for the grouped column. Kept out of the way while there are none,
    // but revealed the moment a card is picked up, since dropping onto it is the only way to
    // clear a value -- unless the column is required, where clearing is not allowed at all.
    const unassignedColumn = useMemo(() => (groupedColumn ? {
        key: EMPTY_ENUM_GROUP_KEY,
        label: groupedColumn.title ? `Unassigned ${groupedColumn.title}` : 'Unassigned',
        filter: { fieldName: groupedFieldName, filterType: 'is_null', filterValues: null },
    } : null), [groupedColumn, groupedFieldName])
    const canClearGroupedColumn = !!groupedColumn && !groupedColumn.isRequired
    const isUnassignedHidden = unassignedTotal === 0 && !(canClearGroupedColumn && !!draggedTask)

    const onColumnTotal = useCallback((columnKey, total) => {
        if (columnKey !== EMPTY_ENUM_GROUP_KEY) return
        setUnassignedTotal(total)
    }, [])

    const onTasksLoaded = useCallback((columnKey, tasks) => {
        setTaskRegistry((prev) => {
            let changed = false
            const next = { ...prev }
            tasks.forEach((task) => {
                if (getComparableTaskSignature(prev[task.id]) !== getComparableTaskSignature(task)) {
                    next[task.id] = task
                    changed = true
                }
            })
            return changed ? next : prev
        })

        setLocalOverrides((prev) => {
            let changed = false
            const next = { ...prev }

            tasks.forEach((task) => {
                if (next[task.id] === columnKey && getTaskGroupKey(task, groupBy) === columnKey) {
                    delete next[task.id]
                    changed = true
                }
            })

            return changed ? next : prev
        })
    }, [groupBy])

    const setColumnsLoading = useCallback((columnKeys, delta) => {
        setMutationLoadingMap((prev) => {
            const next = { ...prev }
            columnKeys.forEach((columnKey) => {
                const currentValue = next[columnKey] || 0
                const nextValue = currentValue + delta
                if (nextValue > 0) next[columnKey] = nextValue
                else delete next[columnKey]
            })
            return next
        })
    }, [])

    const handleDrop = useCallback(async (targetColumnKey) => {
        if (!draggedTask) return
        // Archived tasks cannot change status through Kanban drag and drop.
        if (draggedTask.board?.isArchived) {
            setDraggedTask(null)
            setDragOverColumn(null)
            return
        }
        const taskId = draggedTask.id
        const previousColumnKey = getTaskGroupKey(draggedTask, groupBy)
        if (previousColumnKey === targetColumnKey) {
            setDraggedTask(null)
            setDragOverColumn(null)
            return
        }

        const isClearingGroupedColumn = targetColumnKey === EMPTY_ENUM_GROUP_KEY
        // A required column has no empty state to move a task into, so the unassigned column
        // is a place to drag out of and never into.
        if (groupedColumn && isClearingGroupedColumn && !canClearGroupedColumn) {
            setDraggedTask(null)
            setDragOverColumn(null)
            return
        }
        if (!groupedColumn && draggedTask.isAutomated && targetColumnKey === 'DONE') {
            setDraggedTask(null)
            setDragOverColumn(null)
            return
        }

        const targetColumn = KANBAN_COLUMNS.find((column) => column.key === targetColumnKey)
        const targetStatus = targetColumn?.mutationValue || targetColumnKey
        const targetColumnValue = isClearingGroupedColumn ? '' : targetColumnKey
        const mutationValues = groupedColumn
            ? { columnValues: [{ columnId: groupedColumn.id, value: targetColumnValue }] }
            : { status: targetStatus }
        const loadingColumns = [previousColumnKey, targetColumnKey]

        setColumnsLoading(loadingColumns, 1)
        setTaskRegistry((prev) => ({
            ...prev,
            [taskId]: groupedColumn
                ? withCustomColumnValue(draggedTask, groupedColumn, targetColumnValue)
                : { ...draggedTask, status: targetStatus },
        }))
        setLocalOverrides(prev => ({ ...prev, [taskId]: targetColumnKey }))
        setDraggedTask(null)
        setDragOverColumn(null)

        try {
            await apolloClient.mutate({
                mutation: UpdateTaskMutation,
                variables: { input: { taskId, ...mutationValues } },
                refetchQueries: ['AllTasks'],
            })
        } catch (e) {
            setTaskRegistry((prev) => ({
                ...prev,
                [taskId]: draggedTask,
            }))
            setLocalOverrides(prev => {
                const next = { ...prev }
                delete next[taskId]
                return next
            })
        } finally {
            setColumnsLoading(loadingColumns, -1)
        }
    }, [apolloClient, canClearGroupedColumn, draggedTask, groupBy, groupedColumn, setColumnsLoading])

    const quickUpdateTask = useCallback(async (taskId, values) => {
        try {
            await apolloClient.mutate({
                mutation: UpdateTaskMutation,
                variables: { input: { taskId, ...values } },
                refetchQueries: ['AllTasks'],
            })
        } catch (e) { }
    }, [apolloClient])

    const saveTaskTitle = useCallback(async (taskId) => {
        const title = editTaskTitle.trim()
        const original = taskRegistry[taskId]?.title
        if (title && title !== original) {
            await quickUpdateTask(taskId, { title })
        }
        setEditingTaskId(null)
    }, [editTaskTitle, quickUpdateTask, taskRegistry])

    const onStartEditTitle = useCallback((taskId, title) => {
        setEditingTaskId(taskId)
        setEditTaskTitle(title || '')
    }, [])

    const renderColumn = (column, extraProps = {}) => (
        <KanbanColumn
            key={column.key}
            column={column}
            groupBy={groupBy}
            boardId={boardId}
            searchTerm={searchTerm}
            incidentId={incidentId}
            orderBy={orderBy}
            filters={filters}
            localOverrides={localOverrides}
            taskRegistry={taskRegistry}
            mutationLoadingMap={mutationLoadingMap}
            onTasksLoaded={onTasksLoaded}
            onDrop={handleDrop}
            location={location}
            dragOverColumn={dragOverColumn}
            onDragOverColumn={setDragOverColumn}
            draggedTask={draggedTask}
            onDragStart={(task) => {
                setDraggedTask(task)
                setDragOverColumn(null)
            }}
            onDragEnd={() => {
                setDraggedTask(null)
                setDragOverColumn(null)
            }}
            onCardClick={(taskId) => navigate(buildTaskDetailLocation(location, taskId))}
            onQuickUpdate={quickUpdateTask}
            onStartEditTitle={onStartEditTitle}
            editingTaskId={editingTaskId}
            editTaskTitle={editTaskTitle}
            setEditTaskTitle={setEditTaskTitle}
            saveTaskTitle={saveTaskTitle}
            cardMenuTaskId={cardMenuTaskId}
            setCardMenuTaskId={setCardMenuTaskId}
            menuRef={menuRef}
            dragJustEndedRef={dragJustEndedRef}
            enumColumns={enumColumns}
            {...extraProps}
        />
    )

    return (
        <div className="TaskKanbanView">
            {selectedTaskId && <TaskDetailsSidePanel />}
            {isAwaitingGroupedColumn ? (
                <div className="kanban-loading-bar"><div className="kanban-loading-bar-fill" /></div>
            ) : (
                <div className="kanban-board">
                    {unassignedColumn && renderColumn(unassignedColumn, {
                        isHidden: isUnassignedHidden,
                        onColumnTotal,
                    })}
                    {columns.map((column) => renderColumn(column))}
                </div>
            )}
        </div>
    )
}

export default graphql(BoardQuery, {
    skip: ({ boardId }) => !boardId,
    options: ({ boardId }) => ({
        variables: { id: boardId },
        fetchPolicy: 'cache-first',
    }),
    props: ({ data: { board, loading } }) => ({ board, boardLoading: loading }),
})(withApolloClient(TaskKanbanView))
